import AutoCompleteWithTag from '@/components/autoCompleteWithTag';
import CustomCard from '@/components/customCard';
import { LocationConditionEnum } from '@/constants/enum';
import useLocationOptions from '@/hooks/useLocationOptions';
import useUserPlan from '@/hooks/useUserPlan';
import ruleSlice, { ruleErrorsSelector, ruleSettingsSelector } from '@/redux/slice/rule.slice';
import { IOptions } from '@/types';
import { Badge } from '@shopify/polaris';
import { DeleteIcon } from '@shopify/polaris-icons';
import { Tone } from '@shopify/polaris/build/ts/src/components/Badge';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ConnectBadge from '../ConnectBadge';
import { config } from '@/config';
import { SWEDEN_SHOP_HIDE_COUNTY_IN_STATE } from '@/constants/custom-shop-name';
import checkoutRuleSlice, { checkoutRuleErrorsSelector, checkoutRuleSettingsSelector } from '@/redux/slice/checkoutRule.slice';

interface IProps {
  onDismiss: () => void;
  badgeTone?: Tone;
  hasConnectBadge?: boolean;
  badgeContent?: string;
  isInCheckout?: boolean;
}

const ProvinceCondition = (props: IProps) => {
  const dispatch = useDispatch();
  const { shop } = config;
  const isShopSwedenRemoveCountryInState = shop ? SWEDEN_SHOP_HIDE_COUNTY_IN_STATE.includes(shop) : false;
  const { isPlanLimited } = useUserPlan();
  const dataNormalRule = useSelector(ruleSettingsSelector);
  const dataCheckoutRule = useSelector(checkoutRuleSettingsSelector);
  const dataRuleLocations = useMemo(() => {
    return props.isInCheckout ? dataCheckoutRule.checkoutLocationConditions : dataNormalRule.locationConditions;
  }, [dataCheckoutRule, dataNormalRule, props.isInCheckout]);

  const errorsNormal = useSelector(ruleErrorsSelector);
  const errorsCheckout = useSelector(checkoutRuleErrorsSelector);

  const errors = useMemo(() => {
    return props.isInCheckout ? errorsCheckout : errorsNormal;
  }, [errorsCheckout, errorsNormal, props.isInCheckout]);
  const [state, setState] = useState<IOptions[]>([]);
  const [previewRegion, setPreviewRegion] = useState('');

  const currentSlice = useMemo(() => {
    return props.isInCheckout ? checkoutRuleSlice : ruleSlice;
  }, [props.isInCheckout]);

  const countries = useMemo(() => {
    return dataRuleLocations.find((item) => item.type === LocationConditionEnum.Country)?.value || [];
  }, [dataRuleLocations]);

  const locationOptions = useLocationOptions({
    states: { countryCode: countries.length === 1 ? countries[0].data : '' },
  });

  const regionOption = useMemo(() => {
    const isOnlySelectSweden = countries.length === 1 && countries[0].data === 'SE';
    const selectedValue = state.map((item) => item.value);
    let temp = isPlanLimited
      ? locationOptions.stateOptions.map((item) => ({ ...item, disabled: !selectedValue?.includes(item.value) }))
      : locationOptions.stateOptions;
    if (isOnlySelectSweden && isShopSwedenRemoveCountryInState)
      temp = temp.map((item) => ({ ...item, label: item.label.replaceAll('County', '') }));
    return temp.sort((a, b) => a.label.localeCompare(b.label));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlanLimited, locationOptions.stateOptions, state, JSON.stringify(countries)]);

  const handleSetError = useCallback(
    (msg: string) => {
      dispatch(
        currentSlice.actions.handleSetError({
          value: { field: 'state', msg },
        }),
      );
    },
    [currentSlice.actions, dispatch],
  );

  const handleRegionChange = useCallback(
    (value: IOptions[]) => {
      setState(value);
      const data = value.map((item) => ({ data: item.value, countryStateName: item.label }));
      const indexOfOldProvince = dataRuleLocations.findIndex((item) => item.type === LocationConditionEnum.State);
      const newDataLocationConditions = [...dataRuleLocations];
      if (indexOfOldProvince !== -1)
        newDataLocationConditions.splice(indexOfOldProvince, 1, {
          type: LocationConditionEnum.State,
          value: data,
          order: newDataLocationConditions[indexOfOldProvince].order,
        });
      dispatch(
        currentSlice.actions.handleChange({
          //@ts-ignore-next-line
          key: props.isInCheckout ? 'checkoutLocationConditions' : 'locationConditions',
          value: newDataLocationConditions,
        }),
      );

      if (!value.some((item) => item.value === previewRegion)) {
        setPreviewRegion('');
      }
      if (value.length === 0) {
        handleSetError('Please select at least 1 state/province');
      } else {
        handleSetError('');
      }
    },
    [currentSlice.actions, dataRuleLocations, dispatch, handleSetError, previewRegion, props.isInCheckout],
  );

  const getStateCondition = useCallback(() => {
    if (dataRuleLocations) {
      const stateCondition = dataRuleLocations.find((item) => item.type === LocationConditionEnum.State);

      if (stateCondition?.value?.length && stateCondition?.value?.every((item) => item.data)) return stateCondition;
    }
    return null;
  }, [dataRuleLocations]);

  useEffect(() => {
    const stateCondition = getStateCondition();
    if (stateCondition) {
      const newState = stateCondition.value.map((item) => ({ value: item.data, label: item.countryStateName || '' }));
      setState(newState);
    }
  }, [dataRuleLocations, handleSetError, getStateCondition]);

  return (
    <div>
      {!!props.hasConnectBadge && <ConnectBadge tone={props.badgeTone}>{props.badgeContent}</ConnectBadge>}
      <CustomCard
        title={
          <div>
            <span className="mr-8">{props.isInCheckout ? 'States/Provinces/Counties' : 'States/Provinces'}</span>
            <Badge>{state.length + ''}</Badge>
          </div>
        }
        textClassName="bold-text-styled"
        secondaryActions={{
          onAction: props.onDismiss,
          buttonProps: {
            icon: DeleteIcon,
            tone: 'critical',
            variant: 'plain',
          },
        }}
        collapsible
        defaultExpanded
      >
        <div className="mt-8">
          <AutoCompleteWithTag
            allowMultiple
            options={regionOption}
            tag={state}
            label={props.isInCheckout ? 'Select states/provinces/counties' : 'States/Provinces(s)'}
            selectedOptions={state.map((item) => item.value)}
            setSelectedOptions={handleRegionChange}
            customErrorMessage={errors.find((item) => item.field === 'state')?.msg}
            customHandleSetError={handleSetError}
          />
        </div>
      </CustomCard>
    </div>
  );
};

export default ProvinceCondition;
